import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

import type {
  CreateOutboundCampaignRequest,
  CreateOutboundCampaignResult,
  ListOutboundCampaignsResponse,
  OutboundCampaignStateResponse,
} from '../../../lib/types';

/** Chaves de cache namespaced (evita colisão com outras listas). */
export const outboundCampaignKeys = {
  all: ['outbound-campaigns'] as const,
  list: (status?: string) => ['outbound-campaigns', 'list', status ?? 'all'] as const,
  state: (id: string) => ['outbound-campaigns', 'state', id] as const,
};

/**
 * F4-b — lista de campanhas (Raiz de Tarefa). `enabled` GATEIA O FETCH (não só o
 * render): o caller passa `enabled = hasCap` (cap opt-in `proactive-outbound-campaign`).
 */
export function useOutboundCampaigns(
  enabled: boolean,
  status?: string,
): {
  data: ListOutboundCampaignsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: outboundCampaignKeys.list(status),
    queryFn: async () =>
      (await api.listOutboundCampaigns(status !== undefined ? { status } : undefined)).data,
    enabled,
    staleTime: 15_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * F4-b — estado ao vivo de uma campanha (raiz + agregado on-demand). Polling leve
 * enquanto está em andamento; `enabled` gateia o fetch (cap + id presente).
 */
export function useOutboundCampaignState(
  id: string | undefined,
  enabled: boolean,
): {
  data: OutboundCampaignStateResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: outboundCampaignKeys.state(id ?? ''),
    queryFn: async () => (await api.getOutboundCampaignState(id!)).data,
    enabled: enabled && id !== undefined,
    // Enquanto a campanha não fechou/cancelou, atualiza o agregado ao vivo (não
    // há WS de campanha — polling honesto). Estados terminais param de pollar.
    refetchInterval: (q) => {
      const s = q.state.data?.root.status;
      return s === 'closed' || s === 'cancelled' ? false : 20_000;
    },
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/** F4-b — cria+ativa a campanha. Invalida a lista; toast honesto no sucesso/erro. */
export function useCreateOutboundCampaign(): {
  create: (body: CreateOutboundCampaignRequest) => Promise<CreateOutboundCampaignResult>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (body: CreateOutboundCampaignRequest) =>
      (await api.createOutboundCampaign(body)).data,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: outboundCampaignKeys.all });
      const skipped = result.skippedAtCreation.length;
      useToastStore.getState().push({
        variant: skipped > 0 ? 'info' : 'success',
        title: 'Campanha criada',
        description:
          skipped > 0
            ? `${result.audienceCount} conversas a caminho · ${skipped} alvo(s) pulado(s) (sem identidade).`
            : `${result.audienceCount} conversas a caminho.`,
        durationMs: 4000,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Não foi possível criar a campanha',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });
  return { create: (body) => mutation.mutateAsync(body), isPending: mutation.isPending };
}

/** F4-b — fecho HUMANO da campanha (If-Match). Invalida lista+estado. */
export function useCloseOutboundCampaign(): {
  close: (id: string, version: number, force?: boolean) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (vars: { id: string; version: number; force?: boolean }) => {
      await api.closeOutboundCampaign(vars.id, vars.version, vars.force ?? false);
    },
    onSuccess: (_r, vars) => {
      void queryClient.invalidateQueries({ queryKey: outboundCampaignKeys.all });
      void queryClient.invalidateQueries({ queryKey: outboundCampaignKeys.state(vars.id) });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Campanha encerrada',
        durationMs: 3000,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Não foi possível encerrar',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });
  return {
    close: (id, version, force) =>
      mutation.mutateAsync({ id, version, ...(force !== undefined && { force }) }),
    isPending: mutation.isPending,
  };
}

/** F4-b — cancela a campanha (SOAs pendentes canceladas; If-Match). */
export function useCancelOutboundCampaign(): {
  cancel: (id: string, version: number) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (vars: { id: string; version: number }) => {
      await api.cancelOutboundCampaign(vars.id, vars.version);
    },
    onSuccess: (_r, vars) => {
      void queryClient.invalidateQueries({ queryKey: outboundCampaignKeys.all });
      void queryClient.invalidateQueries({ queryKey: outboundCampaignKeys.state(vars.id) });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Campanha cancelada',
        durationMs: 3000,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Não foi possível cancelar',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });
  return {
    cancel: (id, version) => mutation.mutateAsync({ id, version }),
    isPending: mutation.isPending,
  };
}
